/**
 * A {@link DevelopersSource} with a handful of entries, for the unit tests and the `/__ui`
 * harness (`specs/M8-P4-developers-docs-playground.md` §9).
 *
 * It is a *small* reference on purpose: the assertions are about the page — search, grouping,
 * the badge, the argument table, *Try in playground* — and a fixture that mirrored all 1 346
 * types would prove nothing the four below do not.
 */
import type {
  DevelopersOverview,
  DevelopersSource,
  Guide,
  PlaygroundSetup,
  ReferenceEntry,
  ReferenceIndex,
} from './source';

const entry = (overrides: Partial<ReferenceEntry> & Pick<ReferenceEntry, 'name' | 'kind'>): ReferenceEntry => ({
  description: null,
  summary: overrides.name,
  deprecationReason: null,
  signature: null,
  badge: null,
  badgeNote: null,
  badgeOps: [],
  args: [],
  fields: [],
  enumValues: [],
  possibleTypes: [],
  interfaces: [],
  example: null,
  ...overrides,
});

export const FIXTURE_ENTRIES: ReferenceEntry[] = [
  entry({
    name: 'Query.boards',
    kind: 'query',
    description: 'Get a collection of boards.',
    summary: 'Get a collection of boards.',
    signature: 'boards(\n  ids: [ID!]\n  limit: Int = 25\n): [Board]',
    badge: 'identical',
    badgeOps: ['tier-b-boards-all-fields'],
    args: [
      {
        name: 'ids',
        type: '[ID!]',
        typeName: 'ID',
        description: 'A list of board ids.',
        defaultValue: null,
        required: false,
        deprecationReason: null,
      },
      {
        name: 'limit',
        type: 'Int',
        typeName: 'Int',
        description: 'The number of boards to return.',
        defaultValue: '25',
        required: false,
        deprecationReason: null,
      },
    ],
    example: 'query {\n  boards(limit: 25) {\n    id\n    name\n  }\n}',
  }),
  entry({
    name: 'Query.docs',
    kind: 'query',
    description: 'Get a collection of docs.',
    summary: 'Get a collection of docs.',
    signature: 'docs(\n  limit: Int = 25\n): [Document]',
    badge: 'deviation',
    badgeNote: 'specs/M9-public-api.md §3.2 (Docs)',
    badgeOps: ['tier-b-docs'],
    example: 'query {\n  docs(limit: 25) {\n    id\n  }\n}',
  }),
  entry({
    name: 'Mutation.create_item',
    kind: 'mutation',
    description: 'Create a new item.',
    summary: 'Create a new item.',
    signature: 'create_item(\n  board_id: ID!\n  item_name: String\n): Item',
    badge: 'deviation',
    badgeNote: 'specs/M9-public-api.md §7.0.6',
    badgeOps: ['errors-missing-required-arg'],
    args: [
      {
        name: 'board_id',
        type: 'ID!',
        typeName: 'ID',
        description: 'The board’s unique identifier.',
        defaultValue: null,
        required: true,
        deprecationReason: null,
      },
    ],
    example: 'mutation {\n  create_item(board_id: "1") {\n    id\n  }\n}',
  }),
  entry({
    name: 'Query.app_installs',
    kind: 'query',
    description: 'Get the installs of an app.',
    summary: 'Get the installs of an app.',
    signature: 'app_installs(\n  app_id: ID!\n): [AppInstall]',
    badge: 'not-implemented',
    example: 'query {\n  app_installs(app_id: "1") {\n    app_id\n  }\n}',
  }),
  entry({
    name: 'Board',
    kind: 'object',
    description: 'A board.',
    summary: 'A board.',
    fields: [
      {
        name: 'id',
        type: 'ID!',
        typeName: 'ID',
        description: 'The board’s unique identifier.',
        args: [],
        deprecationReason: null,
      },
      {
        name: 'board_folder_id',
        type: 'ID',
        typeName: 'ID',
        description: 'The board’s folder.',
        args: [],
        deprecationReason: 'Use `folder` instead.',
      },
    ],
  }),
  entry({
    name: 'BoardKind',
    kind: 'enum',
    description: 'The board’s kind.',
    summary: 'The board’s kind.',
    enumValues: [
      { name: 'public', description: 'Visible to everyone.', deprecationReason: null },
      { name: 'private', description: 'Subscribers only.', deprecationReason: null },
    ],
  }),
];

export const FIXTURE_INDEX: ReferenceIndex = {
  version: '2026-10',
  versions: ['2026-10', '2026-07'],
  conformanceRunId: '2026-09-14T11-32-40-635Z',
  counts: { query: 3, mutation: 1, object: 1, input: 0, interface: 0, union: 0, enum: 1, scalar: 0 },
  badgeCounts: { identical: 1, 'shape-ok': 0, deviation: 2, 'not-implemented': 1, untested: 0 },
  entries: FIXTURE_ENTRIES.map((item) => ({
    name: item.name,
    kind: item.kind,
    summary: item.summary,
    badge: item.badge,
    deprecated: item.deprecationReason !== null,
  })),
};

export const FIXTURE_OVERVIEW: DevelopersOverview = {
  baseUrl: 'https://monday.elizeta.com',
  endpoint: 'https://monday.elizeta.com/v2',
  uploadEndpoint: 'https://monday.elizeta.com/v2/file',
  tokenPage: '/me/developers',
  versions: ['2026-10', '2026-07'],
  defaultVersion: '2026-10',
  conformanceRunId: '2026-09-14T11-32-40-635Z',
  badgeCounts: FIXTURE_INDEX.badgeCounts,
  counts: FIXTURE_INDEX.counts,
  downloads: [
    { id: 'schema', name: 'schema.graphql', href: '/developers/schema.graphql', description: 'The SDL.' },
    { id: 'openapi', name: 'openapi.json', href: '/developers/openapi.json', description: 'OpenAPI 3.1.' },
    { id: 'llms', name: 'llms.txt', href: '/developers/llms.txt', description: 'One file for a model.' },
    { id: 'reference', name: 'reference.md', href: '/developers/reference.md', description: 'Every type.' },
  ],
  publicDocs: false,
  canManagePublicDocs: true,
};

export const FIXTURE_GUIDES: Guide[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    lede: 'One endpoint, one header, monday’s own schema.',
    sections: [
      {
        id: 'endpoint',
        title: 'The endpoint',
        blocks: [
          { kind: 'p', text: 'Every call is a `POST` to `https://monday.elizeta.com/v2`.' },
          {
            kind: 'table',
            head: ['What', 'Where'],
            rows: [['GraphQL endpoint', 'https://monday.elizeta.com/v2']],
          },
        ],
      },
      {
        id: 'token',
        title: 'Authentication',
        blocks: [
          { kind: 'code', language: 'bash', title: 'A first call', code: 'curl -s …' },
          { kind: 'note', text: 'A token carries exactly its owner’s rights.' },
        ],
      },
    ],
  },
  {
    id: 'migrating',
    title: 'Migrating from monday.com',
    lede: 'The schema is monday’s.',
    sections: [
      {
        id: 'change',
        title: 'What to change',
        blocks: [
          {
            kind: 'table',
            head: ['', 'monday.com', 'Eli Z Monday'],
            rows: [['Endpoint', 'https://api.monday.com/v2', 'https://monday.elizeta.com/v2']],
          },
        ],
      },
      {
        id: 'unsupported',
        title: 'Not implemented',
        blocks: [{ kind: 'list', items: ['`Query.app_installs` — Get the installs of an app.'] }],
      },
    ],
  },
];

export const FIXTURE_PLAYGROUND: PlaygroundSetup = {
  /* Relative on purpose: the `/__ui` harness is served from a static server on another origin,
     and an absolute endpoint would make the page attempt a cross-origin call it cannot make. */
  endpoint: '/v2',
  proxyEndpoint: '/developers/v2-proxy',
  versions: ['2026-10', '2026-07'],
  defaultVersion: '2026-10',
  credentialTtlSeconds: 3600,
  tokens: [{ id: '1000000000007', name: 'Personal token', lastUsedAt: null, replayable: true }],
  examples: [
    {
      id: 'me',
      title: 'Who am I',
      description: 'The call every integration starts with.',
      kind: 'read',
      document: 'query {\n  me {\n    id\n  }\n}',
      placeholders: [],
    },
    {
      id: 'P4',
      title: 'Write a number',
      description: 'A real write.',
      kind: 'mutation',
      document: 'mutation {\n  change_column_value(board_id: {{boardId}}) {\n    id\n  }\n}',
      placeholders: ['boardId'],
    },
  ],
  idLookup: 'query PlaygroundIds {\n  boards(limit: 1) {\n    id\n  }\n}',
};

export function createFixtureDevelopersSource(
  overrides: Partial<DevelopersSource> = {},
): DevelopersSource {
  let publicDocs = FIXTURE_OVERVIEW.publicDocs;
  return {
    overview: async () => ({ ...FIXTURE_OVERVIEW, publicDocs }),
    reference: async () => FIXTURE_INDEX,
    entry: async (name) => {
      const found = FIXTURE_ENTRIES.find((item) => item.name === name);
      if (!found) throw new Error(`No fixture entry named ${name}`);
      return found;
    },
    guides: async () => FIXTURE_GUIDES,
    playground: async () => FIXTURE_PLAYGROUND,
    mintCredential: async () => ({
      value: 'ezm_fixture_playground',
      expiresAt: new Date(Date.now() + 3_600_000).toISOString(),
      ttlSeconds: 3600,
    }),
    releaseCredential: async () => ({ revoked: 1 }),
    setPublicDocs: async (enabled) => {
      publicDocs = enabled;
      return { enabled };
    },
    ...overrides,
  };
}
